"use client";

import type { ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/cn";
import type { PrimitiveState } from "@/lib/types";
import {
  EmptyState,
  FailedActionState,
  PartialStaleBanner,
  PermissionRestrictedState,
  ProviderDisconnectedState,
  RetryingState,
  SkeletonLine,
} from "./state-parts";

export interface AttentionCardProps {
  /** What needs attention, e.g. "12 covers open at 8:30 PM". */
  title: string;
  /** Why it matters / business context, e.g. revenue or guest impact. */
  description?: string;
  /** Required — an Attention Card must never be purely informational. */
  primaryAction: {
    label: string;
    onClick?: () => void;
  };
  secondaryAction?: { label: string; onClick?: () => void };
  severity?: "warning" | "danger";
  asOfLabel?: string;
  state?: PrimitiveState;
  onRetry?: () => void;
  onReconnect?: () => void;
  providerName?: string;
  icon?: ReactNode;
  className?: string;
}

/**
 * Attention Card — "represents an exception requiring action, never a
 * decorative KPI" (docs/COMPONENT_LIBRARY.md, verbatim). Every rendered
 * variant of this component keeps a primary action visible except the
 * states where taking action would be meaningless (loading) or impossible
 * (permission restricted, provider disconnected) — those states replace the
 * action with the appropriate remediation affordance instead of hiding the
 * exception entirely.
 */
export function AttentionCard({
  title,
  description,
  primaryAction,
  secondaryAction,
  severity = "warning",
  asOfLabel,
  state = "populated",
  onRetry,
  onReconnect,
  providerName,
  icon,
  className,
}: AttentionCardProps) {
  const severityBg = severity === "danger" ? "bg-status-danger-bg" : "bg-status-warning-bg";
  const severityText = severity === "danger" ? "text-status-danger" : "text-status-warning";

  return (
    <div
      className={cn("rounded-card border border-border-subtle bg-surface-primary p-4 shadow-[var(--shadow-subtle)]", className)}
      data-state={state}
      role="group"
      aria-label={`Attention: ${title}`}
    >
      {state === "loading" && (
        <div className="flex flex-col gap-2">
          <SkeletonLine className="h-8 w-8 rounded-control" />
          <SkeletonLine className="h-4 w-3/4" />
          <SkeletonLine className="h-3 w-1/2" />
          <SkeletonLine className="mt-2 h-8 w-28" />
        </div>
      )}

      {state === "empty" && (
        <EmptyState title="Nothing needs attention" description="All caught up — new exceptions will appear here." />
      )}

      {state === "permission-restricted" && <PermissionRestrictedState />}

      {state === "provider-disconnected" && <ProviderDisconnectedState providerName={providerName} onReconnect={onReconnect} />}

      {state === "failed" && <FailedActionState onRetry={onRetry} />}

      {state === "retrying" && <RetryingState description={title} />}

      {(state === "populated" || state === "partial-stale") && (
        <>
          {state === "partial-stale" && <PartialStaleBanner asOf={asOfLabel} />}
          <div className="flex items-start gap-3">
            <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-control", severityBg, severityText)}>
              {icon ?? <AlertTriangle aria-hidden="true" size={18} />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-text-primary">{title}</p>
              {description ? <p className="mt-0.5 text-xs text-text-secondary">{description}</p> : null}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={primaryAction.onClick}
                  className="rounded-control bg-brand-primary px-3 py-2 text-xs font-bold text-brand-on hover:bg-brand-hover"
                >
                  {primaryAction.label}
                </button>
                {secondaryAction ? (
                  <button
                    type="button"
                    onClick={secondaryAction.onClick}
                    className="rounded-control border border-border-strong px-3 py-2 text-xs font-bold text-text-primary hover:bg-surface-secondary"
                  >
                    {secondaryAction.label}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
